import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, FileText, Repeat, Landmark, CalendarCheck } from "lucide-react";
import { CTA } from "@/data/content";
import { useT, useLanguage } from "@/i18n/LanguageContext";

const BULLET_ICONS = [ShieldCheck, FileText, Repeat, Landmark, CalendarCheck];

export const Reservation = () => {
  const t = useT();
  const { lang } = useLanguage();
  const titleLines = CTA.title[lang];

  return (
    <section
      id="rezervisi"
      data-testid="reservation-section"
      className="relative py-24 lg:py-32 wf-mesh"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            data-testid="reservation-headline"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]"
          >
            {titleLines.map((line, i) => (
              <span key={i} className="block">
                {i === CTA.italicIndex ? (
                  <em className="not-italic italic font-light text-[#62646B]">{line}</em>
                ) : (
                  <span className="text-[#0A0A0F]">{line}</span>
                )}
              </span>
            ))}
          </h2>

          <ul data-testid="reservation-bullets" className="mt-10 space-y-4 max-w-lg">
            {CTA.bullets.map((b, i) => {
              const Icon = BULLET_ICONS[i] || ShieldCheck;
              return (
                <li key={i} className="flex gap-3 text-sm text-[#0A0A0F] leading-relaxed">
                  <Icon size={16} className="text-[#0A0A0F] shrink-0 mt-0.5" />
                  {t(b)}
                </li>
              );
            })}
          </ul>

          <p className="text-xs uppercase tracking-[0.25em] text-[#9B9DA4] mt-10">{t(CTA.footnote)}</p>
        </motion.div>

        {/* Big CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="wf-glass rounded-3xl p-8 lg:p-10 border border-[#E8E7E2] wf-glow text-center"
          data-testid="reservation-cta-card"
        >
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#62646B] font-semibold">
            {lang === "sr" ? "Spremni? Pokrenite proces" : "Ready? Start the process"}
          </p>
          <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
            {t(CTA.cta)}
          </h3>
          <p className="text-sm text-[#62646B] mt-4 max-w-sm mx-auto">
            {lang === "sr"
              ? "Četiri jednostavna koraka: lični podaci → predugovor → SEPA depozit → potvrda. Bez novčanika, bez kripto plaćanja, bez obaveza pre KHoV odobrenja."
              : "Four simple steps: personal details → pre-agreement → SEPA deposit → confirmation. No wallet, no crypto payment, no commitment before KHoV approval."}
          </p>

          <Link
            to="/reserve"
            data-testid="reservation-cta-link"
            className="group wf-shimmer mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors duration-300"
          >
            {t(CTA.cta)}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <p className="text-[11px] text-[#9B9DA4] mt-6 leading-relaxed">
            {lang === "sr"
              ? "Depozit se vraća u celosti ako KHoV ne odobri emisiju."
              : "Deposit is fully refunded if KHoV does not approve the issuance."}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
